import React, { useState } from 'react';
import { DocumentIcon, CodeBracketIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import { getFileTypeConfig, isImageFile } from '../utils/fileHelpers';

const FileAttachment = ({ attachment, isProjectChat, userId, onImageClick, isOwnMessage = true, variant = 'single' }) => {
  const fileConfig = getFileTypeConfig(attachment.fileName);
  const [imageError, setImageError] = useState(false);
  
  // Function to get proxied image URL for expired S3 URLs
  const getProxiedImageUrl = (s3Url) => {
    if (!s3Url) return null;
    
    // If it's already not an S3 URL, just return it as is
    if (!s3Url.includes('amazonaws.com')) {
      return s3Url;
    }
    
    // Extract file key from the S3 URL (everything after the bucket name)
    const urlParts = s3Url.split('/');
    const fileKey = urlParts.slice(3).join('/');
    
    // Return URL that will be handled by our backend proxy endpoint
    return `${process.env.REACT_APP_API_URL}/brand/image-proxy?key=${encodeURIComponent(fileKey)}`;
  };
  
  // Determine icon and color based on file type
  const getIconComponent = (iconType) => {
    switch (iconType) {
      case 'code':
        return CodeBracketIcon;
      case 'text':
        return DocumentTextIcon;
      default:
        return DocumentIcon;
    }
  };

  const IconComponent = getIconComponent(fileConfig.iconType);

  const isImage = attachment.type === 'image' || isImageFile(attachment.fileName);

  // For images, render as image
  if (isImage) {
    // Use proxy URL if original failed, otherwise use original
    const imageUrl = imageError ? getProxiedImageUrl(attachment.s3Url) : attachment.s3Url;
    
    const imageClasses = variant === 'grid'
      ? 'chat-attachment-img-squircle w-full aspect-square object-cover cursor-pointer block'
      : `chat-attachment-img-squircle-lg max-h-[300px] object-contain cursor-pointer block ${isOwnMessage ? 'ml-auto' : 'mr-auto'}`;
    
    return (
      <img 
        src={imageUrl}
        alt={attachment.fileName}
        className={imageClasses}
        onError={(e) => {
          if (!imageError) {
            // First error - try the proxy URL
            console.log('S3 URL failed, trying proxy for:', attachment.fileName);
            setImageError(true);
          } else {
            // Proxy also failed - show fallback
            console.error('Both S3 URL and proxy failed for:', attachment.fileName, attachment.s3Url);
            e.target.style.display = 'none';
            // Try to show a fallback message
            const fallback = document.createElement('div');
            fallback.className = 'text-gray-500 dark:text-[#444444] text-sm p-4 border border-gray-300 dark:border-[#444444] rounded-lg';
            fallback.textContent = `Failed to load image: ${attachment.fileName}`;
            e.target.parentNode.appendChild(fallback);
          }
        }}
        onLoad={() => {
          console.log('Successfully loaded image:', attachment.fileName, imageError ? '(via proxy)' : '(direct)');
        }}
        onClick={() => {
          // Open image in modal - use the working URL
          onImageClick(imageUrl, attachment.fileName);
        }}
      />
    );
  }

  // For other files, render as file link with icon
  const fileChipClasses = variant === 'grid'
    ? 'flex items-center gap-2 px-2.5 py-2 bg-[#F9FAFB] dark:bg-[#282828] border border-[#E5E7EB] dark:border-[#444444] rounded-[18px] min-w-[160px] max-w-[240px] hover:opacity-90 transition-opacity no-underline'
    : `inline-flex items-center gap-3 px-2 py-2 pr-4 bg-[#ededed] dark:bg-[#282828] rounded-full ${isOwnMessage ? 'ml-auto' : 'mr-auto'}`;
  
  return (
    <a
      href={attachment.s3Url}
      target="_blank"
      rel="noopener noreferrer"
      className={fileChipClasses}
      title={attachment.fileName}
    >
      <div className={`w-7 h-7 ${fileConfig.bgColor} rounded-full flex items-center justify-center flex-shrink-0`}>
        <IconComponent className="w-4 h-4 text-white" />
      </div>
      <span className="text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white transition-colors flex-1 min-w-0 truncate text-sm">
        {attachment.fileName}
      </span>
    </a>
  );
};

export default FileAttachment; 